import { useEffect, useState, useRef, useCallback } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useToast } from '@/hooks/use-toast';
import { useQueryClient } from '@tanstack/react-query';
import { safeLog } from '@/lib/safeLog';

interface NewRecall {
  id: string;
  product_name: string;
  brand: string;
  risk_type: string;
  severity_score: number;
  published_at: string;
}

export const useRealtimeRecalls = () => {
  const [newRecallsCount, setNewRecallsCount] = useState(0);
  const [latestRecall, setLatestRecall] = useState<NewRecall | null>(null);
  const sessionStartRef = useRef<Date>(new Date());
  const { toast } = useToast();
  const queryClient = useQueryClient();

  // Reset counter and refresh data
  const dismissNewRecalls = useCallback(() => {
    setNewRecallsCount(0);
    setLatestRecall(null);
    sessionStartRef.current = new Date();
    // Invalidate to get fresh data
    queryClient.invalidateQueries({ queryKey: ['recalls-paginated'] });
  }, [queryClient]);

  useEffect(() => {
    const channel = supabase
      .channel('realtime-recalls')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'recalls'
        },
        (payload) => {
          const newRecall = payload.new as NewRecall;
          safeLog.debug('[REALTIME] New recall detected:', newRecall.product_name);
          
          setNewRecallsCount(prev => prev + 1);
          setLatestRecall(newRecall);
          
          // Show non-intrusive toast
          toast({
            title: "🔔 Nouveau rappel",
            description: `${newRecall.product_name} - ${newRecall.brand}`,
            duration: 5000,
          });
          
          // Invalidate query cache to include new data
          queryClient.invalidateQueries({ queryKey: ['recalls-paginated'] });
        }
      )
      .subscribe((status) => {
        safeLog.debug('[REALTIME] Subscription status:', status);
      });

    return () => {
      safeLog.debug('[REALTIME] Cleaning up channel');
      supabase.removeChannel(channel);
    };
  }, [toast, queryClient]);

  return { 
    newRecallsCount, 
    latestRecall,
    dismissNewRecalls 
  };
};
